import React, { useState, useEffect, useMemo } from 'react'
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  NumberInput,
  NumberInputField,
  VStack,
  HStack,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  useToast,
  Spinner,
  Text,
  SimpleGrid,
  Switch,
  Flex,
  Textarea,
  FormHelperText,
  FormErrorMessage,
  IconButton,
  Divider,
  Badge,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Wrap,
  WrapItem,
  Tag,
  TagLabel,
  Radio,
  RadioGroup,
  Stack,
} from '@chakra-ui/react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import Card from 'components/card/Card'
import Dropzone from '../settings/components/Dropzone'
import { useGetEvent, useCreateEvent, useUpdateEvent, useSyncEventTiers } from 'queries/event.query'
import { useGetVenues } from 'queries/venue.query'
import { useGetSeatingPlans } from 'queries/seatingPlan.query'
import { useGetPaymentGateways } from 'queries/paymentGateway.query'
import { MdSave, MdArrowBack, MdAdd, MdDelete } from 'react-icons/md'
import {
  getAllCurrencies,
  getCurrencySymbol,
  getDefaultCurrency,
  getDefaultVatRate,
} from 'utils/currency'
import GuestSeatReservations from './components/GuestSeatReservations'

const defaultTicketTranslations = {
  ticket_type: '',
  attendee: '',
  date_time: '',
  doors_open: '',
  venue: '',
  price: '',
  seat_row: '',
  order_ref: '',
  secure_entry: '',
  notice: '',
}

const TICKET_PRESETS = {
  fa: {
    ticket_type: 'نوع تکت',
    attendee: 'نام دارنده',
    date_time: 'تاریخ و ساعت',
    doors_open: 'بازگشایی درب‌ها',
    venue: 'مکان برگزاری',
    price: 'قیمت',
    seat_row: 'صندلی و ردیف',
    order_ref: 'شماره سفارش',
    secure_entry: 'ورود معتبر',
    notice: 'تکت غیرقابل استرداد است',
  },
  de: {
    ticket_type: 'TICKET-ART',
    attendee: 'TEILNEHMER',
    date_time: 'DATUM & ZEIT',
    doors_open: 'EINLASS',
    venue: 'VERANSTALTUNGSORT',
    price: 'PREIS',
    seat_row: 'SITZ & REIHE',
    order_ref: 'BESTELL-NR.',
    secure_entry: 'SICHERER EINLASS',
    notice: 'Dieses Ticket ist nicht erstattungsfähig',
  }
}

const formatVenueLocation = (venue) => {
  if (!venue) return ''

  return [
    venue.name,
    venue.address,
    [venue.postal_code, venue.city].filter(Boolean).join(' '),
    venue.country,
  ].filter(Boolean).join(', ')
}

const getPlanTierTemplates = (plan, previousTiers, vatRate) => {
  const categorySeatCounts = new Map()
  const categories = plan?.plan_json?.categories || []

  for (const zone of plan?.plan_json?.zones || []) {
    for (const row of zone.rows || []) {
      for (const seat of row.seats || []) {
        const categoryName = String(seat.category || '').trim()
        if (categoryName) {
          categorySeatCounts.set(categoryName, (categorySeatCounts.get(categoryName) || 0) + 1)
        }
      }
    }
  }

  const matchedTiers = new Set()
  const sortedPreviousTiers = [...previousTiers].sort((a, b) => (a.display_order || 0) - (b.display_order || 0))

  return categories.map((category, index) => {
    const categoryName = String(category.name || '').trim()
    
    // 1. Try exact UUID match
    let existing = sortedPreviousTiers.find(tier => 
      !matchedTiers.has(tier.id) &&
      tier.plan_category_uuid && category.uuid && 
      tier.plan_category_uuid === category.uuid
    )

    // 2. Try exact name match
    if (!existing) {
      existing = sortedPreviousTiers.find(tier =>
        !matchedTiers.has(tier.id) &&
        String(tier.name || '').trim().toLowerCase() === categoryName.toLowerCase()
      )
    }

    // 3. Fallback: match by color
    if (!existing) {
      existing = sortedPreviousTiers.find(tier => 
        !matchedTiers.has(tier.id) &&
        tier.color && category.color &&
        tier.color.toLowerCase() === category.color.toLowerCase()
      )
    }

    // 4. Fallback: match by index
    if (!existing) {
      existing = sortedPreviousTiers.find(tier => 
        !matchedTiers.has(tier.id) &&
        tier.display_order === index
      )
    }

    // 5. Last Fallback: Just grab the first unmatched tier
    if (!existing) {
       existing = sortedPreviousTiers.find(tier => !matchedTiers.has(tier.id))
    }

    if (existing && existing.id) {
      matchedTiers.add(existing.id)
    }

    let preservedPrice = '';
    if (existing && existing.price !== undefined && existing.price !== null && String(existing.price).trim() !== '') {
      preservedPrice = existing.price;
    } else {
      preservedPrice = '';
    }

    return {
      id: existing?.id || null,
      plan_category_uuid: category.uuid || existing?.plan_category_uuid || null,
      name: categoryName,
      color: category.color || existing?.color || '#0756C9',
      total_quota: categorySeatCounts.get(categoryName) || 0,
      price: preservedPrice,
      vat_rate: existing?.vat_rate ?? vatRate,
      description: existing?.description || '',
      display_order: index,
      allowed_gates: existing?.allowed_gates || ['all'],
    }
  }).filter(tier => tier.name && tier.total_quota > 0)
}

export default function EventForm() {
  const { id } = useParams()
  const isEdit = !!id
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const initialTab = Number(searchParams.get('tab') || 0)
  const [activeTab, setActiveTab] = useState(initialTab === 1 ? 1 : 0)
  const toast = useToast()

  useEffect(() => {
    setActiveTab(Number(searchParams.get('tab') || 0) === 1 ? 1 : 0)
  }, [searchParams])

  // Queries
  const { data: eventData, isLoading: isLoadingEvent } = useGetEvent(id)
  const { data: venuesData, isLoading: isLoadingVenues } = useGetVenues(1, 100)
  const { data: gatewaysData } = useGetPaymentGateways()
  const paymentGateways = gatewaysData?.data || (Array.isArray(gatewaysData) ? gatewaysData : [])
  const stripeGateways = paymentGateways.filter(g => g.provider === 'stripe' && g.is_active)
  const paypalGateways = paymentGateways.filter(g => g.provider === 'paypal' && g.is_active)

  // Mutations
  const { mutateAsync: createEvent, isPending: isCreating } = useCreateEvent()
  const { mutateAsync: updateEvent, isPending: isUpdating } = useUpdateEvent()
  const { mutateAsync: syncTiers, isPending: isSyncing } = useSyncEventTiers()

  // Form State - Basic Info
  const [formData, setFormData] = useState({
    title: '',
    short_description: '',
    description: '',
    start_datetime: '',
    end_datetime: '',
    doors_open_datetime: '',
    booking_start_datetime: '',
    booking_end_datetime: '',
    location: '',
    venue_id: '',
    seating_plan_id: '',
    seating_mode: 'interactive_seats',
    status: 'PUBLISHED',
    category: 'concert',
    vat_rate: getDefaultVatRate() || '7.00',
    max_tickets_per_order: 10,
    ticket_checkout_type: 'internal',
    external_ticket_url: '',
    currency: getDefaultCurrency() || 'EUR',
    stripe_gateway_id: '',
    paypal_gateway_id: '',
    is_ticketing_enabled: true,
    ticket_secondary_language: 'none',
  })

  const { data: plansData, isLoading: isLoadingPlans } = useGetSeatingPlans(1, 100, formData.venue_id || null)
  const selectedSeatingPlan = useMemo(
    () => plansData?.data?.find(plan => String(plan.id) === String(formData.seating_plan_id)),
    [plansData, formData.seating_plan_id],
  )
  const isExternalCheckout = formData.ticket_checkout_type === 'external'
  const hasInternalTicketing = !isExternalCheckout
    && formData.is_ticketing_enabled !== false

  const [coverImageFile, setCoverImageFile] = useState(null)
  const [coverImagePreview, setCoverImagePreview] = useState(null)
  const [coverImageMobileFile, setCoverImageMobileFile] = useState(null)
  const [coverImageMobilePreview, setCoverImageMobilePreview] = useState(null)
  const [ticketImageFile, setTicketImageFile] = useState(null)
  const [ticketImagePreview, setTicketImagePreview] = useState(null)
  const [imageError, setImageError] = useState(null)
  const [validationErrors, setValidationErrors] = useState({})

  // Form State - Pricing (Ticket Tiers)
  const [pricingTiers, setPricingTiers] = useState([])
  const [draftGuestSeatGuids, setDraftGuestSeatGuids] = useState([])
  const [customCurrencyMode, setCustomCurrencyMode] = useState(false)

  // Form State - Ticket Custom Translations (Passport Style)
  const [ticketCustomTranslations, setTicketCustomTranslations] = useState(defaultTicketTranslations)
  const [hasCustomTicketLang, setHasCustomTicketLang] = useState(false)

  // Form State - Schedule & Lineup
  const [programAgenda, setProgramAgenda] = useState([])
  const [lineup, setLineup] = useState([])

  // Populate form on edit
  useEffect(() => {
    if (isEdit && eventData?.data) {
      const e = eventData.data
      setFormData({
        title: e.title || '',
        short_description: e.short_description || '',
        description: e.description || '',
        start_datetime: e.start_datetime ? e.start_datetime.substring(0, 16) : '',
        end_datetime: e.end_datetime ? e.end_datetime.substring(0, 16) : '',
        doors_open_datetime: e.doors_open_datetime ? e.doors_open_datetime.substring(0, 16) : '',
        booking_start_datetime: e.booking_start_datetime ? e.booking_start_datetime.substring(0, 16) : '',
        booking_end_datetime: e.booking_end_datetime ? e.booking_end_datetime.substring(0, 16) : '',
        location: e.location || '',
        venue_id: e.venue_id || '',
        seating_plan_id: e.seating_plan_id || '',
        seating_mode: e.seating_mode || 'interactive_seats',
        status: e.status || 'PUBLISHED',
        category: e.category || 'concert',
        vat_rate: e.vat_rate != null ? String(e.vat_rate) : (getDefaultVatRate() || '7.00'),
        max_tickets_per_order: e.max_tickets_per_order || 10,
        ticket_checkout_type: e.ticket_checkout_type || (e.external_ticket_url ? 'external' : 'internal'),
        external_ticket_url: e.external_ticket_url || '',
        currency: e.currency || getDefaultCurrency() || 'EUR',
        stripe_gateway_id: e.stripe_gateway_id || '',
        paypal_gateway_id: e.paypal_gateway_id || '',
        is_ticketing_enabled: e.is_ticketing_enabled ?? true,
        ticket_secondary_language: e.ticket_secondary_language || 'none',
      })
      const eventCurrency = e.currency || getDefaultCurrency() || 'EUR'
      const availableCurrencies = getAllCurrencies()
      const isKnown = availableCurrencies.some(c => c.code === eventCurrency)
      setCustomCurrencyMode(!isKnown)
      if (e.ticket_custom_translations) {
        try {
          const parsed = typeof e.ticket_custom_translations === 'string'
            ? JSON.parse(e.ticket_custom_translations)
            : e.ticket_custom_translations
          if (parsed && typeof parsed === 'object') {
            const translations = { ...defaultTicketTranslations, ...parsed }
            const legacyNoticeTranslations = {
              'پول واپس داده نمی‌شود • غیرقابل استرداد': 'تکت غیرقابل استرداد است',
              'Keine Rückerstattung • Vom Umtausch ausgeschlossen': 'Dieses Ticket ist nicht erstattungsfähig',
            }
            translations.notice = legacyNoticeTranslations[translations.notice] || translations.notice
            setTicketCustomTranslations(translations)
            setHasCustomTicketLang(true)
          }
        } catch (err) {
          console.error('Error parsing ticket_custom_translations:', err)
        }
      } else if (e.ticket_secondary_language && e.ticket_secondary_language !== 'none') {
        setHasCustomTicketLang(true)
        if (e.ticket_secondary_language === 'fa') {
          setTicketCustomTranslations(TICKET_PRESETS.fa)
        } else if (e.ticket_secondary_language === 'de') {
          setTicketCustomTranslations(TICKET_PRESETS.de)
        } else if (e.ticket_secondary_language === 'nl') {
          setTicketCustomTranslations(TICKET_PRESETS.nl)
        }
      }
      if (e.ticket_tiers && e.ticket_tiers.length > 0) {
        setPricingTiers(e.ticket_tiers)
      } else if (e.seating_mode === 'general_admission' || !e.seating_plan_id) {
        setPricingTiers([{
          id: null,
          name: 'General Admission',
          color: '#0756C9',
          price: '',
          vat_rate: e.vat_rate != null ? String(e.vat_rate) : (getDefaultVatRate() || '7.00'),
          total_quota: '',
          display_order: 0,
          allowed_gates: ['all'],
        }])
      }
      if (Array.isArray(e.program_agenda)) {
        setProgramAgenda(e.program_agenda)
      }
      if (Array.isArray(e.lineup)) {
        setLineup(e.lineup)
      }
      if (e.cover_image) {
        if (e.cover_image.startsWith('http')) {
          setCoverImagePreview(e.cover_image);
        } else {
          const baseUrl = process.env.REACT_APP_PUBLIC_BACKEND_URL || (process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL.replace(/\/api\/?$/, '') : 'http://localhost:8000')
          const cleanPath = e.cover_image.startsWith('/') ? e.cover_image.substring(1) : e.cover_image;
          const finalPath = cleanPath.startsWith('storage/') ? cleanPath : `storage/${cleanPath}`;
          setCoverImagePreview(`${baseUrl}/${finalPath}`)
        }
      }
      if (e.cover_image_mobile) {
        if (e.cover_image_mobile.startsWith('http')) {
          setCoverImageMobilePreview(e.cover_image_mobile);
        } else {
          const baseUrl = process.env.REACT_APP_PUBLIC_BACKEND_URL || (process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL.replace(/\/api\/?$/, '') : 'http://localhost:8000')
          const cleanPath = e.cover_image_mobile.startsWith('/') ? e.cover_image_mobile.substring(1) : e.cover_image_mobile;
          const finalPath = cleanPath.startsWith('storage/') ? cleanPath : `storage/${cleanPath}`;
          setCoverImageMobilePreview(`${baseUrl}/${finalPath}`)
        }
      }
      if (e.ticket_image) {
        if (e.ticket_image.startsWith('http')) {
          setTicketImagePreview(e.ticket_image);
        } else {
          const baseUrl = process.env.REACT_APP_PUBLIC_BACKEND_URL || (process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL.replace(/\/api\/?$/, '') : 'http://localhost:8000')
          const cleanPath = e.ticket_image.startsWith('/') ? e.ticket_image.substring(1) : e.ticket_image;
          const finalPath = cleanPath.startsWith('storage/') ? cleanPath : `storage/${cleanPath}`;
          setTicketImagePreview(`${baseUrl}/${finalPath}`)
        }
      }
    }
  }, [eventData, isEdit])

  const handleCoverImageChange = (file) => {
    const actualFile = file?.target?.files?.[0] || file;
    if (actualFile && actualFile instanceof File) {
      setCoverImageFile(actualFile)
      setImageError(null)
      setValidationErrors(previous => ({ ...previous, cover_image: undefined }))
      if (coverImagePreview && !coverImagePreview.startsWith('http')) {
        URL.revokeObjectURL(coverImagePreview)
      }
      setCoverImagePreview(URL.createObjectURL(actualFile))
    }
  }

  const handleCoverImageMobileChange = (file) => {
    const actualFile = file?.target?.files?.[0] || file;
    if (actualFile && actualFile instanceof File) {
      setCoverImageMobileFile(actualFile)
      setValidationErrors(previous => ({ ...previous, cover_image_mobile: undefined }))
      if (coverImageMobilePreview && !coverImageMobilePreview.startsWith('http')) {
        URL.revokeObjectURL(coverImageMobilePreview)
      }
      setCoverImageMobilePreview(URL.createObjectURL(actualFile))
    }
  }

  const handleTicketImageChange = (file) => {
    const actualFile = file?.target?.files?.[0] || file;
    if (actualFile && actualFile instanceof File) {
      setTicketImageFile(actualFile)
      setImageError(null)
      setValidationErrors(previous => ({ ...previous, ticket_image: undefined }))
      if (ticketImagePreview && !ticketImagePreview.startsWith('http')) {
        URL.revokeObjectURL(ticketImagePreview)
      }
      setTicketImagePreview(URL.createObjectURL(actualFile))
    }
  }

  useEffect(() => {
    const venue = venuesData?.data?.find(item => String(item.id) === String(formData.venue_id))
    if (venue) {
      setFormData(previous => ({
        ...previous,
        location: formatVenueLocation(venue),
      }))
    }
  }, [formData.venue_id, venuesData])

  // Extract ticket categories and capacities when seating plan changes.
  useEffect(() => {
    if (formData.seating_plan_id && plansData?.data) {
      const plan = plansData.data.find(p => String(p.id) === String(formData.seating_plan_id))
      if (plan) {
        setPricingTiers(previousTiers => getPlanTierTemplates(plan, previousTiers, formData.vat_rate))
      }
    }
  }, [formData.seating_plan_id, plansData, formData.vat_rate])

  const selectedVenue = useMemo(() => {
    return venuesData?.data?.find(item => String(item.id) === String(formData.venue_id))
  }, [venuesData, formData.venue_id])

  const venueGates = useMemo(() => {
    if (!selectedVenue || !Array.isArray(selectedVenue.gates)) return []
    return selectedVenue.gates
      .map(g => (typeof g === 'object' && g ? g.name : String(g)))
      .filter(Boolean)
  }, [selectedVenue])

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setValidationErrors(previous => ({ ...previous, [name]: undefined }))

    if (
      name === 'seating_plan_id' ||
      name === 'venue_id' ||
      (name === 'seating_mode' && value !== 'interactive_seats') ||
      (name === 'ticket_checkout_type' && value !== 'internal')
    ) {
      setDraftGuestSeatGuids([])
    }

    if (name === 'seating_plan_id') {
      if (!value && formData.seating_mode === 'interactive_seats') setActiveTab(0)
      setFormData(previous => ({ ...previous, seating_plan_id: value }))
      return
    }

    if (name === 'venue_id') {
      const venue = venuesData?.data?.find(item => String(item.id) === String(value))
      setFormData(previous => ({
        ...previous,
        venue_id: value,
        seating_plan_id: '',
        location: formatVenueLocation(venue),
      }))
      return
    }

    if (name === 'seating_mode') {
      if (value === 'general_admission') {
        setFormData(previous => ({ ...previous, seating_mode: value, seating_plan_id: '' }))
        setPricingTiers(prev => (prev && prev.length > 0) ? prev : [{
          id: null,
          name: 'General Admission',
          color: '#0756C9',
          price: '',
          vat_rate: formData.vat_rate,
          total_quota: '',
          display_order: 0,
          allowed_gates: ['all'],
        }])
      } else {
        setFormData(previous => ({ ...previous, seating_mode: value }))
      }
      return
    }

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
  }

  const handleTierChange = (index, field, value) => {
    const newTiers = [...pricingTiers]
    newTiers[index][field] = value
    setPricingTiers(newTiers)
  }

  const handleAddTier = () => {
    setPricingTiers([
      ...pricingTiers,
      {
        id: null,
        name: `Tier ${pricingTiers.length + 1}`,
        color: '#0756C9',
        price: '',
        vat_rate: formData.vat_rate,
        total_quota: '',
        display_order: pricingTiers.length,
        allowed_gates: ['all'],
      }
    ])
  }

  const handleRemoveTier = (index) => {
    const newTiers = [...pricingTiers]
    newTiers.splice(index, 1)
    setPricingTiers(newTiers)
  }

  // Program Agenda Handlers
  const handleAddAgenda = () => {
    setProgramAgenda([...programAgenda, { time: '', activity: '' }])
  }
  const handleRemoveAgenda = (index) => {
    setProgramAgenda(programAgenda.filter((_, idx) => idx !== index))
  }
  const handleAgendaChange = (index, field, value) => {
    const updated = [...programAgenda]
    updated[index][field] = value
    setProgramAgenda(updated)
  }

  // Lineup Handlers
  const handleAddLineup = () => {
    setLineup([...lineup, { name: '', role: '' }])
  }
  const handleRemoveLineup = (index) => {
    setLineup(lineup.filter((_, idx) => idx !== index))
  }
  const handleLineupChange = (index, field, value) => {
    const updated = [...lineup]
    updated[index][field] = value
    setLineup(updated)
  }

  const handleSave = async () => {
    setValidationErrors({})
    // Frontend Validation - Basic Info
    if (!formData.title || !formData.start_datetime || !formData.venue_id) {
      toast({
        title: 'Validation Error',
        description: 'Please fill in all required fields (Title, Start Date, Venue).',
        status: 'warning',
        duration: 4000,
        isClosable: true,
      })
      return
    }

    if (isExternalCheckout && !formData.external_ticket_url?.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Please enter the External Ticket URL (e.g. Partner link: https://event-partner.com/...).',
        status: 'warning',
        duration: 4000,
        isClosable: true,
      })
      return
    }

    if (hasInternalTicketing) {
      if (!formData.booking_start_datetime) {
        toast({
          title: 'Validation Error',
          description: 'Please select a valid Ticket Sales Open date. (Note: November has only 30 days: 30/11/2026 or 01/12/2026).',
          status: 'warning',
          duration: 6000,
          isClosable: true,
        })
        return
      }
      if (!formData.booking_end_datetime) {
        toast({
          title: 'Validation Error',
          description: 'Please select a valid Ticket Sales Close date.',
          status: 'warning',
          duration: 5000,
          isClosable: true,
        })
        return
      }
    }

    // Frontend Validation - Ticket Pricing (only when using internal platform)
    if (hasInternalTicketing && pricingTiers.length === 0) {
      toast({
        title: 'Validation Error',
        description: formData.seating_mode === 'interactive_seats'
          ? 'The selected Seating Plan must contain assigned ticket categories and seats.'
          : 'Please add at least one ticket tier for general admission.',
        status: 'warning',
        duration: 4000,
        isClosable: true,
      })
      return
    }

    if (hasInternalTicketing && pricingTiers.length > 0) {
      const missingPrices = pricingTiers.filter(tier => tier.price === '' || tier.price === null || tier.price === undefined)
      if (missingPrices.length > 0) {
        toast({
          title: 'Validation Error',
          description: 'Please enter a price for all ticket categories.',
          status: 'warning',
          duration: 4000,
          isClosable: true,
        })
        return
      }
    }

    // Frontend Validation - File Size
    if (coverImageFile || ticketImageFile) {
      const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/svg+xml', 'image/webp']

      if (coverImageFile) {
        if (coverImageFile.size > 2 * 1024 * 1024) { // 2MB
          toast({
            title: 'File too large',
            description: 'The cover image must not exceed 2MB in size.',
            status: 'error',
            duration: 4000,
            isClosable: true,
          })
          return
        }
        if (!allowedTypes.includes(coverImageFile.type)) {
          toast({
            title: 'Invalid File Type',
            description: 'Only JPG, PNG, WEBP and SVG files are allowed.',
            status: 'error',
            duration: 4000,
            isClosable: true,
          })
          return
        }
      }

      if (ticketImageFile) {
        if (ticketImageFile.size > 2 * 1024 * 1024) { // 2MB
          toast({
            title: 'File too large',
            description: 'The ticket image must not exceed 2MB in size.',
            status: 'error',
            duration: 4000,
            isClosable: true,
          })
          return
        }
        if (!allowedTypes.includes(ticketImageFile.type)) {
          toast({
            title: 'Invalid File Type',
            description: 'Only JPG, PNG, WEBP and SVG files are allowed.',
            status: 'error',
            duration: 4000,
            isClosable: true,
          })
          return
        }
      }

    }

    try {
      let savedEventId = id

      // We must use FormData because the endpoint might expect it (due to images)
      const data = new FormData()
      Object.keys(formData).forEach(key => {
        // Exclude seating_plan_id if general admission or external checkout
        if (key === 'seating_plan_id' && (formData.seating_mode === 'general_admission' || isExternalCheckout)) {
          return;
        }
        // Booking dates apply only when internal ticketing is used
        if (isExternalCheckout && (key === 'booking_start_datetime' || key === 'booking_end_datetime')) {
          return
        }
        // Currency and VAT rate are handled externally if external checkout is chosen
        if (isExternalCheckout && (key === 'vat_rate' || key === 'currency')) {
          return
        }
        if (key === 'is_ticketing_enabled') {
          data.append(key, !isExternalCheckout ? '1' : '0')
          return
        }
        if (key === 'external_ticket_url') {
          data.append(key, isExternalCheckout ? (formData.external_ticket_url || '') : '')
          return
        }
        if (key === 'ticket_secondary_language' || key === 'ticket_custom_translations') {
          return;
        }

        if (formData[key] !== null && formData[key] !== '') {
          // For boolean fields, send 1 or 0
          if (typeof formData[key] === 'boolean') {
            data.append(key, formData[key] ? '1' : '0')
          } else {
            data.append(key, formData[key])
          }
        } else if (isEdit && (key === 'doors_open_datetime' || key === 'end_datetime' || key === 'external_ticket_url')) {
          data.append(key, '')
        }
      })

      // Append ticket dual-language settings
      if (hasCustomTicketLang) {
        data.append('ticket_custom_translations', JSON.stringify(ticketCustomTranslations))
        data.append('ticket_secondary_language', 'custom')
      } else {
        data.append('ticket_custom_translations', '')
        data.append('ticket_secondary_language', 'none')
      }

      if (coverImageFile) {
        data.append('cover_image', coverImageFile)
      }

      if (coverImageMobileFile) {
        data.append('cover_image_mobile', coverImageMobileFile)
      }

      if (ticketImageFile) {
        data.append('ticket_image', ticketImageFile)
      }

      // Append Program Agenda
      const cleanAgenda = programAgenda.filter(item => item.time?.trim() || item.activity?.trim())
      data.append('program_agenda', JSON.stringify(cleanAgenda))

      // Append Lineup
      const cleanLineup = lineup.filter(item => item.name?.trim())
      data.append('lineup', JSON.stringify(cleanLineup))

      if (isEdit) {
        data.append('id', id)
        data.append('_method', 'PUT') // Spoof PUT method for Laravel when using FormData
        await updateEvent(data)
      } else {
        const res = await createEvent(data)
        savedEventId = res?.data?.id
      }

      // Save Pricing Tiers only for internal ticketing
      if (hasInternalTicketing && pricingTiers.length > 0) {
        await syncTiers({ eventId: savedEventId, tiers: pricingTiers })
      }

      toast({
        title: 'Event saved successfully',
        status: 'success',
      })

      if (!isEdit && savedEventId && hasInternalTicketing && formData.seating_mode === 'interactive_seats') {
        navigate(`/admin/events/${savedEventId}/edit?tab=1`)
      } else {
        navigate('/admin/events')
      }
    } catch (error) {
      const responseData = error?.response?.data
      const errors = responseData?.errors || {}
      const errorMessages = Object.values(errors).flat().filter(Boolean)

      setValidationErrors(errors)
      toast({
        title: responseData?.message || 'Error saving event',
        status: 'error',
        description: errorMessages.join(' ') || error.message,
        duration: 7000,
        isClosable: true,
      })
    }
  }

  if ((isEdit && isLoadingEvent) || isLoadingVenues || isLoadingPlans) {
    return <Flex justify="center" p={10}><Spinner size="xl" /></Flex>
  }

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
      <Card p={6}>
        <Flex justify="space-between" align="center" mb={6}>
          <Button leftIcon={<MdArrowBack />} variant="ghost" onClick={() => navigate('/admin/events')}>
            Back to Events
          </Button>
          <Text fontSize="2xl" fontWeight="bold">
            {isEdit ? 'Edit Event' : 'Create Event'}
          </Text>
          <Button
            colorScheme="brand"
            leftIcon={<MdSave />}
            onClick={handleSave}
            isLoading={isCreating || isUpdating || isSyncing}
          >
            Save Event
          </Button>
        </Flex>

        {Object.keys(validationErrors).length > 0 && (
          <Alert status="error" mb={6} borderRadius="md" alignItems="flex-start">
            <AlertIcon mt={1} />
            <Box>
              <AlertTitle>Please correct the highlighted fields.</AlertTitle>
              <AlertDescription>
                <VStack align="start" spacing={1} mt={2}>
                  {Object.entries(validationErrors).flatMap(([field, messages]) =>
                    (messages || []).map((message, index) => (
                      <Text key={`${field}-${index}`} fontSize="sm">{message}</Text>
                    ))
                  )}
                </VStack>
              </AlertDescription>
            </Box>
          </Alert>
        )}

        <Tabs variant="enclosed" colorScheme="brand" index={activeTab} onChange={setActiveTab}>
          <TabList>
            <Tab>Event Details</Tab>
            {hasInternalTicketing && (
              <Tab isDisabled={formData.seating_mode === 'interactive_seats' && !formData.seating_plan_id}>
                Ticket Pricing & Seats
              </Tab>
            )}
            <Tab>Schedule & Lineup</Tab>
          </TabList>

          <TabPanels>
            {/* TAB 1: BASIC INFO */}
            <TabPanel>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                <FormControl isRequired isInvalid={!!validationErrors.title}>
                  <FormLabel>Event Title</FormLabel>
                  <Input name="title" value={formData.title} onChange={handleChange} placeholder="e.g. Summer Concert 2026" />
                  <FormErrorMessage>{validationErrors.title?.[0]}</FormErrorMessage>
                </FormControl>

                <FormControl isRequired isInvalid={!!validationErrors.category}>
                  <FormLabel>Category</FormLabel>
                  <Select name="category" value={formData.category} onChange={handleChange}>
                    <option value="concert">Concert</option>
                    <option value="sports">Sports</option>
                    <option value="cultural">Cultural</option>
                    <option value="conference">Conference</option>
                    <option value="other">Other</option>
                  </Select>
                  <FormErrorMessage>{validationErrors.category?.[0]}</FormErrorMessage>
                </FormControl>

                <FormControl>
                  <FormLabel>Short Description</FormLabel>
                  <Input name="short_description" value={formData.short_description} onChange={handleChange} placeholder="Brief summary of the event" />
                </FormControl>

                <FormControl>
                  <FormLabel>Full Description</FormLabel>
                  <Textarea name="description" value={formData.description} onChange={handleChange} placeholder="Detailed information about the event" rows={4} />
                </FormControl>

                <FormControl isInvalid={!!imageError}>
                  <Flex justify="space-between" align="center" mb={2}>
                    <FormLabel mb="0">Official Event Poster</FormLabel>
                    <Text fontSize="xs" color="gray.500">Standard: 1140×640px (16:9) or 1140×855px (4:3) - Fits all devices</Text>
                  </Flex>
                  <Dropzone
                    w="100%"
                    mb="0px"
                    title='Upload Official Event Poster'
                    preview={coverImagePreview}
                    onFileChange={handleCoverImageChange}
                  />
                  {imageError && (
                    <Text color='red.500' fontSize='sm' mt={2}>
                      {imageError}
                    </Text>
                  )}
                </FormControl>

                <FormControl>
                  <Flex justify="space-between" align="center" mb={2}>
                    <FormLabel mb="0">Mobile Poster (Optional)</FormLabel>
                    <Text fontSize="xs" color="gray.500">Only used for narrow screens if uploaded</Text>
                  </Flex>
                  <Dropzone
                    w="100%"
                    mb="0px"
                    title='Upload Mobile Poster (Optional)'
                    preview={coverImageMobilePreview}
                    onFileChange={handleCoverImageMobileChange}
                  />
                </FormControl>

                <FormControl isRequired isInvalid={!!validationErrors.start_datetime}>
                  <FormLabel>Start Date & Time</FormLabel>
                  <Input name="start_datetime" type="datetime-local" value={formData.start_datetime} onChange={handleChange} />
                  <FormErrorMessage>{validationErrors.start_datetime?.[0]}</FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={!!validationErrors.end_datetime}>
                  <FormLabel>End Date & Time (Optional)</FormLabel>
                  <Input name="end_datetime" type="datetime-local" value={formData.end_datetime} onChange={handleChange} />
                  <FormErrorMessage>{validationErrors.end_datetime?.[0]}</FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={!!validationErrors.doors_open_datetime}>
                  <FormLabel>Doors Open (Optional)</FormLabel>
                  <Input name="doors_open_datetime" type="datetime-local" value={formData.doors_open_datetime} onChange={handleChange} />
                  <FormErrorMessage>{validationErrors.doors_open_datetime?.[0]}</FormErrorMessage>
                </FormControl>

                <FormControl isRequired isInvalid={!!validationErrors.venue_id}>
                  <FormLabel>Venue</FormLabel>
                  <Select name="venue_id" value={formData.venue_id} onChange={handleChange} placeholder="Select Venue">
                    {venuesData?.data?.map(v => (
                      <option key={v.id} value={v.id}>{v.name}</option>
                    ))}
                  </Select>
                  <FormErrorMessage>{validationErrors.venue_id?.[0]}</FormErrorMessage>
                </FormControl>

                {/* Ticketing Checkout Method (Right after Venue and before Seating Plan) */}
                <FormControl gridColumn={{ base: 'span 1', md: 'span 2' }}>
                  <FormLabel fontWeight="bold" fontSize="sm" mb={2}>
                    Ticketing Checkout Method
                  </FormLabel>
                  <RadioGroup
                    value={formData.ticket_checkout_type || 'internal'}
                    onChange={(val) => setFormData(prev => ({
                      ...prev,
                      ticket_checkout_type: val,
                      external_ticket_url: val === 'internal' ? '' : prev.external_ticket_url,
                    }))}
                  >
                    <Stack direction={{ base: 'column', md: 'row' }} spacing={4}>
                      <Box
                        p={4}
                        borderRadius="xl"
                        borderWidth="2px"
                        borderColor={formData.ticket_checkout_type === 'internal' ? 'brand.500' : 'gray.200'}
                        bg={formData.ticket_checkout_type === 'internal' ? 'blue.50' : 'white'}
                        _dark={{
                          bg: formData.ticket_checkout_type === 'internal' ? 'blue.900' : 'gray.800',
                          borderColor: formData.ticket_checkout_type === 'internal' ? 'blue.400' : 'gray.700',
                        }}
                        cursor="pointer"
                        flex={1}
                        onClick={() => setFormData(prev => ({ ...prev, ticket_checkout_type: 'internal', external_ticket_url: '' }))}
                      >
                        <Radio value="internal" colorScheme="blue" mb={1}>
                          <Text fontWeight="bold" fontSize="sm">
                            Internal Ticketing Platform
                          </Text>
                        </Radio>
                        <Text fontSize="xs" color="gray.500" pl={6}>
                          Interactive seating plan, built-in checkout, and native PDF tickets.
                        </Text>
                      </Box>

                      <Box
                        p={4}
                        borderRadius="xl"
                        borderWidth="2px"
                        borderColor={formData.ticket_checkout_type === 'external' ? 'orange.400' : 'gray.200'}
                        bg={formData.ticket_checkout_type === 'external' ? 'orange.50' : 'white'}
                        _dark={{
                          bg: formData.ticket_checkout_type === 'external' ? 'orange.900' : 'gray.800',
                          borderColor: formData.ticket_checkout_type === 'external' ? 'orange.400' : 'gray.700',
                        }}
                        cursor="pointer"
                        flex={1}
                        onClick={() => setFormData(prev => ({ ...prev, ticket_checkout_type: 'external' }))}
                      >
                        <Radio value="external" colorScheme="orange" mb={1}>
                          <Text fontWeight="bold" fontSize="sm">
                            External Ticketing Platform (Partner / Custom URL)
                          </Text>
                        </Radio>
                        <Text fontSize="xs" color="gray.500" pl={6}>
                          Redirect buyers directly to your external ticketing provider when clicking &ldquo;Buy Tickets&rdquo;.
                        </Text>
                      </Box>
                    </Stack>
                  </RadioGroup>
                </FormControl>

                {/* If External Ticketing: External Ticket URL */}
                {formData.ticket_checkout_type === 'external' && (
                  <FormControl
                    gridColumn={{ base: 'span 1', md: 'span 2' }}
                    isRequired
                    isInvalid={!!validationErrors.external_ticket_url}
                  >
                    <FormLabel fontWeight="bold" fontSize="sm">
                      External Ticket URL
                    </FormLabel>
                    <Input
                      name="external_ticket_url"
                      value={formData.external_ticket_url || ''}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        external_ticket_url: e.target.value,
                      }))}
                      placeholder="https://example.com/tickets/..."
                      borderColor="orange.400"
                      _focus={{ borderColor: 'orange.500', boxShadow: '0 0 0 1px #DD6B20' }}
                    />
                    <FormHelperText fontSize="xs" color="gray.500">
                      Enter the direct event link on the external platform. Ticket booking and payments will be handled externally.
                    </FormHelperText>
                  </FormControl>
                )}

                {/* If Internal Ticketing: Seating Mode, Seating Plan & Booking Period */}
                {formData.ticket_checkout_type === 'internal' && (
                  <>
                    <FormControl isRequired>
                      <FormLabel>Seating Mode</FormLabel>
                      <Select name="seating_mode" value={formData.seating_mode} onChange={handleChange}>
                        <option value="interactive_seats">With Seating Plan</option>
                        <option value="general_admission">Without Seating Plan (General Admission)</option>
                      </Select>
                    </FormControl>

                    {formData.seating_mode === 'interactive_seats' && (
                      <FormControl isInvalid={!!validationErrors.seating_plan_id}>
                        <FormLabel>Seating Plan</FormLabel>
                        <Select name="seating_plan_id" value={formData.seating_plan_id} onChange={handleChange} placeholder="Select Seating Plan">
                          {plansData?.data?.map(p => (
                            <option key={p.id} value={p.id}>{p.name}</option>
                          ))}
                        </Select>
                        <FormErrorMessage>{validationErrors.seating_plan_id?.[0]}</FormErrorMessage>
                      </FormControl>
                    )}

                    {/* Booking Period (Internal Ticketing Only) */}
                    {hasInternalTicketing && <>
                    <FormControl isRequired isInvalid={!!validationErrors.booking_start_datetime}>
                      <FormLabel>Ticket Sales Open (Booking Start)</FormLabel>
                      <Input
                        name="booking_start_datetime"
                        type="datetime-local"
                        value={formData.booking_start_datetime}
                        onChange={handleChange}
                      />
                      <FormHelperText fontSize="xs" color="gray.500">
                        When ticket sales officially open to the public. Required for internal ticketing.
                      </FormHelperText>
                      <FormErrorMessage>{validationErrors.booking_start_datetime?.[0]}</FormErrorMessage>
                    </FormControl>

                    <FormControl isRequired isInvalid={!!validationErrors.booking_end_datetime}>
                      <FormLabel>Ticket Sales Close (Booking End)</FormLabel>
                      <Input
                        name="booking_end_datetime"
                        type="datetime-local"
                        value={formData.booking_end_datetime}
                        onChange={handleChange}
                      />
                      <FormHelperText fontSize="xs" color="gray.500">
                        When online sales stop (e.g. 2 hours before showtime). Required for internal ticketing.
                      </FormHelperText>
                      <FormErrorMessage>{validationErrors.booking_end_datetime?.[0]}</FormErrorMessage>
                    </FormControl>
                    </>}

                    {/* Internal Ticketing Only: Currency & VAT Rate */}
                    <FormControl isRequired isInvalid={!!validationErrors.currency}>
                      <Flex justify="space-between" align="center" mb={1}>
                        <FormLabel mb={0}>Event Currency</FormLabel>
                        <Badge colorScheme="purple" fontSize="11px" px={2} borderRadius="full">
                          Symbol: {getCurrencySymbol(formData.currency || 'EUR')}
                        </Badge>
                      </Flex>
                      <Select
                        value={customCurrencyMode ? 'CUSTOM' : (formData.currency || 'EUR')}
                        onChange={(e) => {
                          const val = e.target.value
                          if (val === 'CUSTOM') {
                            setCustomCurrencyMode(true)
                          } else {
                            setCustomCurrencyMode(false)
                            setFormData(prev => ({ ...prev, currency: val }))
                          }
                        }}
                      >
                        {getAllCurrencies().map(c => (
                          <option key={c.code} value={c.code}>
                            {c.flag || '🌐'} {c.name}
                          </option>
                        ))}
                        <option value="CUSTOM">➕ Other / Custom Currency Code...</option>
                      </Select>

                      {customCurrencyMode && (
                        <Input
                          mt={2}
                          placeholder="e.g. PLN, QAR, KWD, BRL, SGD, NZD"
                          maxLength={5}
                          value={formData.currency}
                          onChange={(e) => setFormData(prev => ({ ...prev, currency: e.target.value.toUpperCase().trim() }))}
                        />
                      )}
                      <FormHelperText fontSize="xs" color="gray.500">
                        Active: <strong>{formData.currency || 'EUR'} ({getCurrencySymbol(formData.currency)})</strong>. Used for internal ticketing, pricing, checkout, and invoices.
                      </FormHelperText>
                      <FormErrorMessage>{validationErrors.currency?.[0]}</FormErrorMessage>
                    </FormControl>

                    <FormControl isRequired isInvalid={!!validationErrors.vat_rate}>
                      <Flex justify="space-between" align="center" mb={1}>
                        <FormLabel mb={0}>Booking Fee (%)</FormLabel>
                        <Badge colorScheme="green" fontSize="10px" px={2} borderRadius="full">
                          Standard Fee: 7%
                        </Badge>
                      </Flex>
                      <NumberInput value={formData.vat_rate ?? '7.00'} onChange={(v) => handleChange({ target: { name: 'vat_rate', value: v } })}>
                        <NumberInputField name="vat_rate" onChange={handleChange} />
                      </NumberInput>
                      <FormHelperText fontSize="xs" color="gray.500">
                        Default 7.00% booking fee applied at checkout.
                      </FormHelperText>
                      <FormErrorMessage>{validationErrors.vat_rate?.[0]}</FormErrorMessage>
                    </FormControl>
                  </>
                )}

                <FormControl isRequired isInvalid={!!validationErrors.status}>
                  <FormLabel>Status</FormLabel>
                  <Select name="status" value={formData.status} onChange={handleChange}>
                    <option value="PUBLISHED">Published</option>
                    <option value="DRAFT">Draft</option>
                    <option value="CANCELLED">Cancelled</option>
                    <option value="POSTPONED">Postponed</option>
                    <option value="COMPLETED">Completed</option>
                  </Select>
                  <FormErrorMessage>{validationErrors.status?.[0]}</FormErrorMessage>
                </FormControl>

                <FormControl>
                  <FormLabel>Location / Hall</FormLabel>
                  <Input value={formData.location || ''} isReadOnly placeholder="Select a venue to fill this automatically" />
                  <FormHelperText fontSize="xs" color="gray.500">Venue name and address are filled automatically from the selected venue.</FormHelperText>
                </FormControl>

                {/* Additional Internal Ticketing Controls */}
                {hasInternalTicketing && (
                  <>
                    <FormControl>
                      <FormLabel>Max Tickets Per Order</FormLabel>
                      <NumberInput value={formData.max_tickets_per_order ?? 10} onChange={(val) => handleChange({ target: { name: 'max_tickets_per_order', value: val } })}>
                        <NumberInputField name="max_tickets_per_order" />
                      </NumberInput>
                      <FormHelperText fontSize="xs" color="gray.500">Maximum tickets a customer can purchase in one order (Default: 10).</FormHelperText>
                    </FormControl>

                    <FormControl>
                      <Flex justify="space-between" align="center" mb={2}>
                        <FormLabel mb="0">Ticket Background Image</FormLabel>
                        <Text fontSize="xs" color="gray.500">Recommended: 600x1200px (Vertical PDF)</Text>
                      </Flex>
                      <Dropzone
                        w="100%"
                        mb="0px"
                        title='Upload Ticket Image'
                        preview={ticketImagePreview}
                        onFileChange={handleTicketImageChange}
                      />
                    </FormControl>

                    <FormControl>
                      <FormLabel>Stripe Payment Account</FormLabel>
                      <Select
                        name="stripe_gateway_id"
                        value={formData.stripe_gateway_id || ''}
                        onChange={handleChange}
                      >
                        <option value="">(Global Default Stripe Gateway)</option>
                        {stripeGateways.map(gw => (
                          <option key={gw.id} value={gw.id}>
                            {gw.name} ({gw.mode.toUpperCase()}) {gw.is_default ? '★ Default' : ''}
                          </option>
                        ))}
                      </Select>
                      <FormHelperText fontSize="xs" color="gray.500">
                        Select a dedicated Stripe account for this event, or leave blank to use the global default.
                      </FormHelperText>
                    </FormControl>

                    <FormControl>
                      <FormLabel>PayPal Payment Account</FormLabel>
                      <Select
                        name="paypal_gateway_id"
                        value={formData.paypal_gateway_id || ''}
                        onChange={handleChange}
                      >
                        <option value="">(Global Default PayPal Gateway)</option>
                        {paypalGateways.map(gw => (
                          <option key={gw.id} value={gw.id}>
                            {gw.name} ({gw.mode.toUpperCase()}) {gw.is_default ? '★ Default' : ''}
                          </option>
                        ))}
                      </Select>
                      <FormHelperText fontSize="xs" color="gray.500">
                        Select a dedicated PayPal account for this event, or leave blank to use the global default.
                      </FormHelperText>
                    </FormControl>
                  </>
                )}
              </SimpleGrid>

              {/* TICKET DUAL-LANGUAGE (PASSPORT STYLE) SECTION */}
              {hasInternalTicketing && (
                <>
                  <Divider my={8} />
                  <Box
                    p={5}
                    borderRadius="xl"
                    borderWidth="1px"
                    borderColor="gray.200"
                    _dark={{ borderColor: 'gray.700', bg: 'navy.800' }}
                    bg="gray.50"
                  >
                    <Flex justify="space-between" align={{ base: 'start', md: 'center' }} direction={{ base: 'column', md: 'row' }} gap={4} mb={4}>
                      <Box>
                        <HStack spacing={2} mb={1}>
                          <Text fontSize="lg" fontWeight="bold">
                            Ticket Dual-Language & Custom Words
                          </Text>
                          <Badge colorScheme={hasCustomTicketLang ? 'green' : 'gray'}>
                            {hasCustomTicketLang ? 'Active' : 'English Only'}
                          </Badge>
                        </HStack>
                        <Text fontSize="sm" color="gray.600" _dark={{ color: 'gray.400' }}>
                          Tickets & PDF downloads always display English by default. Enable this to add a second language or custom words/sentences separated by a slash (e.g. <b>DATE & TIME / تاریخ و ساعت</b>).
                        </Text>
                      </Box>

                      <FormControl display="flex" alignItems="center" w="auto">
                        <FormLabel htmlFor="enable_ticket_custom_lang" mb="0" fontSize="sm" fontWeight="bold">
                          Enable Dual-Language
                        </FormLabel>
                        <Switch
                          id="enable_ticket_custom_lang"
                          isChecked={hasCustomTicketLang}
                          onChange={(e) => {
                            const enabled = e.target.checked
                            setHasCustomTicketLang(enabled)
                            if (enabled && !Object.values(ticketCustomTranslations).some(Boolean)) {
                              setTicketCustomTranslations(TICKET_PRESETS.fa)
                            }
                          }}
                          colorScheme="brand"
                          size="lg"
                        />
                      </FormControl>
                    </Flex>

                    {hasCustomTicketLang && (
                      <Box mt={4} pt={4} borderTopWidth="1px" borderColor="gray.200" _dark={{ borderColor: 'gray.700' }}>
                        <Flex justify="space-between" align={{ base: 'start', sm: 'center' }} direction={{ base: 'column', sm: 'row' }} gap={3} mb={5}>
                          <Text fontSize="xs" fontWeight="bold" color="gray.500" textTransform="uppercase">
                            Quick Preset Auto-Fill:
                          </Text>
                          <HStack spacing={2} flexWrap="wrap">
                            <Button
                              size="xs"
                              variant="outline"
                              colorScheme="brand"
                              onClick={() => setTicketCustomTranslations(TICKET_PRESETS.fa)}
                            >
                              Fill Persian / دری
                            </Button>
                            <Button
                              size="xs"
                              variant="outline"
                              colorScheme="brand"
                              onClick={() => setTicketCustomTranslations(TICKET_PRESETS.de)}
                            >
                              Fill German
                            </Button>
                            <Button
                              size="xs"
                              variant="ghost"
                              colorScheme="red"
                              onClick={() => setTicketCustomTranslations(defaultTicketTranslations)}
                            >
                              Clear All
                            </Button>
                          </HStack>
                        </Flex>

                        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                          {[
                            { key: 'ticket_type', label: 'Ticket Type Label', en: 'TICKET TYPE', placeholder: 'e.g. نوع تکت or TICKET-ART' },
                            { key: 'attendee', label: 'Attendee Name Label', en: 'ATTENDEE', placeholder: 'e.g. نام دارنده or TEILNEHMER' },
                            { key: 'date_time', label: 'Date & Time Label', en: 'DATE & TIME', placeholder: 'e.g. تاریخ و ساعت or DATUM & ZEIT' },
                            { key: 'doors_open', label: 'Doors Open Label', en: 'DOORS OPEN', placeholder: 'e.g. بازگشایی درب‌ها or EINLASS' },
                            { key: 'venue', label: 'Venue Label', en: 'VENUE', placeholder: 'e.g. مکان برگزاری or VERANSTALTUNGSORT' },
                            { key: 'price', label: 'Price Label', en: 'PRICE', placeholder: 'e.g. قیمت or PREIS' },
                            { key: 'seat_row', label: 'Seat & Row Label', en: 'SEAT & ROW', placeholder: 'e.g. صندلی و ردیف or SITZ & REIHE' },
                            { key: 'order_ref', label: 'Order Ref Label', en: 'ORDER REF', placeholder: 'e.g. شماره سفارش or BESTELL-NR.' },
                            { key: 'secure_entry', label: 'Secure Entry Label', en: 'SECURE ENTRY', placeholder: 'e.g. ورود معتبر or SICHERER EINLASS' },
                            { key: 'notice', label: 'Non-Refundable Ticket Notice', en: 'NON-REFUNDABLE TICKET', placeholder: 'e.g. تکت غیرقابل استرداد است' },
                          ].map((item) => (
                            <FormControl key={item.key}>
                              <Flex justify="space-between" align="baseline" mb={1}>
                                <FormLabel mb={0} fontSize="sm" fontWeight="semibold">
                                  {item.label}
                                </FormLabel>
                                <Badge fontSize="10px" colorScheme="blue" variant="subtle">
                                  Preview: {item.en}{ticketCustomTranslations[item.key] ? ` / ${ticketCustomTranslations[item.key]}` : ''}
                                </Badge>
                              </Flex>
                              <Input
                                placeholder={item.placeholder}
                                value={ticketCustomTranslations[item.key] || ''}
                                onChange={(e) =>
                                  setTicketCustomTranslations((prev) => ({
                                    ...prev,
                                    [item.key]: e.target.value,
                                  }))
                                }
                                bg="white"
                                _dark={{ bg: 'navy.900' }}
                              />
                            </FormControl>
                          ))}
                        </SimpleGrid>
                      </Box>
                    )}
                  </Box>
                </>
              )}
            </TabPanel>

            {/* TAB 2: TICKET PRICING & SEATS (INTERNAL TICKETING ONLY) */}
            {hasInternalTicketing && (
              <TabPanel>
                <VStack spacing={6} align="stretch">
                  {formData.seating_mode === 'interactive_seats' && !formData.seating_plan_id ? (
                    <Alert status="warning" borderRadius="md">
                      <AlertIcon />
                      <Text fontSize="sm">Please select a seating plan in the Event Details tab first.</Text>
                    </Alert>
                  ) : (
                    <VStack spacing={4} align="stretch">
                      {/* Currency & Tax Summary Card */}
                      <Card p={4} bg="purple.50" _dark={{ bg: 'whiteAlpha.100' }} borderWidth="1px" borderColor="purple.200" borderRadius="lg" mb={2}>
                        <Flex justify="space-between" align={{ base: 'start', md: 'center' }} direction={{ base: 'column', md: 'row' }} gap={3}>
                          <HStack spacing={3}>
                            <Badge colorScheme="purple" fontSize="md" px={3} py={1} borderRadius="md">
                              {formData.currency || 'EUR'} ({getCurrencySymbol(formData.currency)})
                            </Badge>
                            <Box>
                              <Text fontWeight="bold" fontSize="sm">
                                Event Currency: {formData.currency || 'EUR'} &bull; Booking Fee: {formData.vat_rate || '7.00'}%
                              </Text>
                              <Text fontSize="xs" color="gray.600" _dark={{ color: 'gray.400' }}>
                                All prices below are gross in {formData.currency || 'EUR'}. Currency & default booking fee can also be managed in Settings &gt; Currencies & Fiscal.
                              </Text>
                            </Box>
                          </HStack>
                          <Button size="xs" variant="outline" colorScheme="purple" onClick={() => setActiveTab(0)}>
                            Edit Currency / Booking Fee in Tab 1
                          </Button>
                        </Flex>
                      </Card>

                      <Flex justify="space-between" align="center" mb={2}>
                        <Text>
                          {formData.seating_mode === 'interactive_seats' && formData.seating_plan_id
                            ? 'Set the prices for the categories defined in the selected Seating Plan.'
                            : 'Define the ticket tiers (e.g. VIP, General Admission, Standard) for your event.'}
                        </Text>
                        {(formData.seating_mode === 'general_admission' || !formData.seating_plan_id) && (
                          <Button size="sm" colorScheme="brand" onClick={handleAddTier}>+ Add Tier / Category</Button>
                        )}
                      </Flex>

                      {pricingTiers.length === 0 && (formData.seating_mode === 'general_admission' || !formData.seating_plan_id) && (
                        <Alert status="info" borderRadius="md">
                          <AlertIcon />
                          <Box flex="1">
                            <Text fontSize="sm" fontWeight="bold">No Ticket Categories Defined Yet</Text>
                            <Text fontSize="xs">Click "+ Add Tier / Category" above to create ticket categories with pricing and total capacity.</Text>
                          </Box>
                          <Button size="xs" colorScheme="brand" onClick={handleAddTier}>+ Add Category</Button>
                        </Alert>
                      )}

                      {pricingTiers.map((tier, index) => (
                        <Card key={index} p={4} bg="gray.50" _dark={{ bg: 'gray.800' }}>
                          <Flex align="center" justify="space-between" mb={4}>
                            <HStack flex={1}>
                              <Box w="16px" h="16px" borderRadius="full" bg={tier.color || '#0756C9'} />
                              {formData.seating_mode === 'interactive_seats' && formData.seating_plan_id ? (
                                <Text fontWeight="bold" fontSize="lg">{tier.name}</Text>
                              ) : (
                                <Input
                                  value={tier.name}
                                  onChange={(e) => handleTierChange(index, 'name', e.target.value)}
                                  placeholder="Tier Name (e.g. VIP, Standard)"
                                  size="sm"
                                  maxW="200px"
                                  fontWeight="bold"
                                />
                              )}
                              {(formData.seating_mode === 'general_admission' || !formData.seating_plan_id) && (
                                <Input
                                  type="color"
                                  value={tier.color || '#0756C9'}
                                  onChange={(e) => handleTierChange(index, 'color', e.target.value)}
                                  size="sm"
                                  w="50px"
                                  p={0}
                                />
                              )}
                            </HStack>
                            {(formData.seating_mode === 'general_admission' || !formData.seating_plan_id) && (
                              <Button size="xs" colorScheme="red" variant="ghost" onClick={() => handleRemoveTier(index)}>
                                Remove
                              </Button>
                            )}
                          </Flex>
                          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                            <FormControl isRequired>
                              <FormLabel>Gross Price ({formData.currency || 'EUR'} {getCurrencySymbol(formData.currency)})</FormLabel>
                              <NumberInput value={tier.price ?? ''} onChange={(val) => handleTierChange(index, 'price', val)}>
                                <NumberInputField placeholder="e.g. 50.00" />
                              </NumberInput>
                            </FormControl>
                            {(formData.seating_mode === 'general_admission' || !formData.seating_plan_id) && (
                              <FormControl isRequired>
                                <FormLabel>Total Tickets (Capacity)</FormLabel>
                                <NumberInput value={tier.total_quota ?? ''} onChange={(val) => handleTierChange(index, 'total_quota', val)}>
                                  <NumberInputField placeholder="Capacity e.g. 200" />
                                </NumberInput>
                              </FormControl>
                            )}
                            {formData.seating_mode === 'interactive_seats' && formData.seating_plan_id && (
                              <FormControl>
                                <FormLabel>Tickets from Plan</FormLabel>
                                <Input value={tier.total_quota} isReadOnly />
                                <FormHelperText fontSize="xs">Derived from seats assigned to this category.</FormHelperText>
                              </FormControl>
                            )}
                            <FormControl>
                              <FormLabel>Description (Optional)</FormLabel>
                              <Input value={tier.description || ''} onChange={(e) => handleTierChange(index, 'description', e.target.value)} placeholder="e.g. VIP Lounge Access" />
                            </FormControl>
                          </SimpleGrid>

                          {/* Gate Authorization per Ticket Tier */}
                          <Box mt={3} pt={3} borderTop="1px dashed" borderColor="gray.200" _dark={{ borderColor: 'gray.700' }}>
                            <Flex justify="space-between" align="center" mb={2} wrap="wrap" gap={2}>
                              <Box>
                                <Text fontSize="xs" fontWeight="bold" color="gray.700" _dark={{ color: 'gray.200' }}>
                                  Allowed Entrance & Exit Gates
                                </Text>
                                <Text fontSize="xs" color="gray.500">
                                  Specify which venue gates this ticket tier is authorized to pass through.
                                </Text>
                              </Box>
                              {venueGates.length > 0 && (
                                <Button
                                  size="xs"
                                  variant="ghost"
                                  colorScheme="brand"
                                  onClick={() => {
                                    const current = Array.isArray(tier.allowed_gates) ? tier.allowed_gates : ['all']
                                    const isAll = current.includes('all') || current.length === 0
                                    handleTierChange(index, 'allowed_gates', isAll ? [...venueGates] : ['all'])
                                  }}
                                >
                                  {(Array.isArray(tier.allowed_gates) && !tier.allowed_gates.includes('all') && tier.allowed_gates.length > 0)
                                    ? 'Reset to All Gates'
                                    : 'Select Specific Gates'}
                                </Button>
                              )}
                            </Flex>

                            {venueGates.length === 0 ? (
                              <Text fontSize="xs" color="gray.500" fontStyle="italic">
                                All gates permitted. (To restrict gates, define doors in the selected Venue first).
                              </Text>
                            ) : (
                              <Wrap spacing={2}>
                                <WrapItem>
                                  <Tag
                                    size="sm"
                                    variant={(tier.allowed_gates?.includes('all') || !tier.allowed_gates || tier.allowed_gates.length === 0) ? 'solid' : 'outline'}
                                    colorScheme="purple"
                                    cursor="pointer"
                                    borderRadius="full"
                                    px={3}
                                    py={1}
                                    onClick={() => handleTierChange(index, 'allowed_gates', ['all'])}
                                  >
                                    <TagLabel fontWeight="bold">All Gates</TagLabel>
                                  </Tag>
                                </WrapItem>
                                {venueGates.map((gate) => {
                                  const isSelected = Array.isArray(tier.allowed_gates) && !tier.allowed_gates.includes('all') && tier.allowed_gates.includes(gate)
                                  return (
                                    <WrapItem key={gate}>
                                      <Tag
                                        size="sm"
                                        variant={isSelected ? 'solid' : 'subtle'}
                                        colorScheme={isSelected ? 'teal' : 'gray'}
                                        cursor="pointer"
                                        borderRadius="full"
                                        px={3}
                                        py={1}
                                        onClick={() => {
                                          let current = Array.isArray(tier.allowed_gates) ? [...tier.allowed_gates] : ['all']
                                          if (current.includes('all')) current = []
                                          if (current.includes(gate)) {
                                            current = current.filter(g => g !== gate)
                                            if (current.length === 0) current = ['all']
                                          } else {
                                            current.push(gate)
                                          }
                                          handleTierChange(index, 'allowed_gates', current)
                                        }}
                                      >
                                        <TagLabel fontWeight={isSelected ? 'bold' : 'normal'}>
                                          {isSelected ? `✓ ${gate}` : gate}
                                        </TagLabel>
                                      </Tag>
                                    </WrapItem>
                                  )
                                })}
                              </Wrap>
                            )}
                          </Box>
                        </Card>
                      ))}
                    </VStack>
                  )}
                  {formData.seating_mode === 'interactive_seats' && formData.seating_plan_id && (
                    isEdit ? (
                      <GuestSeatReservations
                        eventId={id}
                        planJson={selectedSeatingPlan?.plan_json}
                        ticketTiers={pricingTiers}
                        bookingStartDatetime={formData.booking_start_datetime}
                        draftGuestSeatGuids={draftGuestSeatGuids}
                        onDraftChange={setDraftGuestSeatGuids}
                      />
                    ) : (
                      <Alert
                        status="info"
                        variant="subtle"
                        flexDirection="column"
                        alignItems="flex-start"
                        borderRadius="xl"
                        p={5}
                        border="1px solid"
                        borderColor="blue.200"
                        _dark={{ bg: 'blue.900/20', borderColor: 'blue.800' }}
                      >
                        <Flex alignItems="center" gap={2} mb={1}>
                          <AlertIcon />
                          <AlertTitle fontSize="sm" fontWeight="bold">
                            Seat Reservation Disabled During Event Creation
                          </AlertTitle>
                        </Flex>
                        <AlertDescription fontSize="xs" color="gray.600" _dark={{ color: 'gray.300' }}>
                          To prevent accidental reservations while setting up details, seat reservation is unavailable during event creation. Once you save and create this event, you can manage and reserve guest seats in <b>Edit Event</b> at any time.
                        </AlertDescription>
                      </Alert>
                    )
                  )}
                </VStack>
              </TabPanel>
            )}

            {/* TAB 3: SCHEDULE & LINEUP */}
            <TabPanel>
              <VStack spacing={8} align="stretch">
                {/* SECTION 1: LINEUP (ARTISTS & PERFORMERS) */}
                <Card p={5} bg="gray.50" border="1px solid" borderColor="gray.200" _dark={{ bg: 'gray.800', borderColor: 'gray.700' }}>
                  <Flex justify="space-between" align="center" mb={4}>
                    <Box>
                      <Text fontWeight="bold" fontSize="lg">Artists & Lineup</Text>
                      <Text fontSize="xs" color="gray.500">Add performers, vocalists, and orchestra members for this event.</Text>
                    </Box>
                    <Button size="sm" colorScheme="brand" leftIcon={<MdAdd />} onClick={handleAddLineup}>
                      Add Artist / Performer
                    </Button>
                  </Flex>

                  {lineup.length === 0 ? (
                    <Box textAlign="center" py={6} px={4} border="1px dashed" borderColor="gray.300" _dark={{ borderColor: 'gray.600' }} borderRadius="md">
                      <Text fontSize="sm" color="gray.500">
                        No performers added yet. Click &quot;+ Add Performer&quot; above to add artists or orchestra members.
                      </Text>
                    </Box>
                  ) : (
                    <VStack spacing={3} align="stretch">
                      {lineup.map((artist, index) => (
                        <Card key={index} p={3} bg="white" _dark={{ bg: 'gray.900' }} shadow="sm">
                          <Flex gap={3} align="center">
                            <Badge colorScheme="blue" borderRadius="full" px={2.5} py={1}>
                              #{index + 1}
                            </Badge>
                            <FormControl isRequired flex={2}>
                              <Input
                                size="sm"
                                placeholder="Artist / Performer Name (e.g. Dawood Sarkhosh)"
                                value={artist.name || ''}
                                onChange={(e) => handleLineupChange(index, 'name', e.target.value)}
                              />
                            </FormControl>
                            <FormControl flex={2}>
                              <Input
                                size="sm"
                                placeholder="Role / Instrument (e.g. Vocalist & Dambura)"
                                value={artist.role || ''}
                                onChange={(e) => handleLineupChange(index, 'role', e.target.value)}
                              />
                            </FormControl>
                            <IconButton
                              size="sm"
                              icon={<MdDelete />}
                              colorScheme="red"
                              variant="ghost"
                              aria-label="Remove performer"
                              onClick={() => handleRemoveLineup(index)}
                            />
                          </Flex>
                        </Card>
                      ))}
                    </VStack>
                  )}
                </Card>

                {/* SECTION 2: PROGRAM AGENDA (SCHEDULE) */}
                <Card p={5} bg="gray.50" border="1px solid" borderColor="gray.200" _dark={{ bg: 'gray.800', borderColor: 'gray.700' }}>
                  <Flex justify="space-between" align="center" mb={4}>
                    <Box>
                      <Text fontWeight="bold" fontSize="lg">Program Agenda</Text>
                      <Text fontSize="xs" color="gray.500">Define the timeline of activities and stages (e.g. 18:30 - Opening Ceremony).</Text>
                    </Box>
                    <Button size="sm" colorScheme="brand" leftIcon={<MdAdd />} onClick={handleAddAgenda}>
                      Add Program Item
                    </Button>
                  </Flex>

                  {programAgenda.length === 0 ? (
                    <Box textAlign="center" py={6} px={4} border="1px dashed" borderColor="gray.300" _dark={{ borderColor: 'gray.600' }} borderRadius="md">
                      <Text fontSize="sm" color="gray.500">
                        No schedule items added yet. Click &quot;+ Add Schedule Item&quot; to build the agenda.
                      </Text>
                    </Box>
                  ) : (
                    <VStack spacing={3} align="stretch">
                      {programAgenda.map((item, index) => (
                        <Card key={index} p={3} bg="white" _dark={{ bg: 'gray.900' }} shadow="sm">
                          <Flex gap={3} align="center">
                            <Badge colorScheme="purple" borderRadius="full" px={2.5} py={1}>
                              #{index + 1}
                            </Badge>
                            <FormControl isRequired maxW="150px">
                              <Input
                                size="sm"
                                placeholder="Time (e.g. 18:30)"
                                value={item.time || ''}
                                onChange={(e) => handleAgendaChange(index, 'time', e.target.value)}
                              />
                            </FormControl>
                            <FormControl isRequired flex={3}>
                              <Input
                                size="sm"
                                placeholder="Activity / Section (e.g. Opening Speech, Performance, Intermission)"
                                value={item.activity || ''}
                                onChange={(e) => handleAgendaChange(index, 'activity', e.target.value)}
                              />
                            </FormControl>
                            <IconButton
                              size="sm"
                              icon={<MdDelete />}
                              colorScheme="red"
                              variant="ghost"
                              aria-label="Remove agenda item"
                              onClick={() => handleRemoveAgenda(index)}
                            />
                          </Flex>
                        </Card>
                      ))}
                    </VStack>
                  )}
                </Card>
              </VStack>
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Card>
    </Box>
  )
}
